<template>
  <div class="store-page">
    <div v-if="loadingSeller" class="store-loading">
      <i class="fas fa-spinner fa-spin fa-2x"></i>
    </div>

    <div v-else-if="sellerError" class="store-error">
      <i class="fas fa-store-slash"></i>
      <h5>Tienda no disponible</h5>
      <p>{{ sellerError }}</p>
    </div>

    <template v-else>
      <header class="store-header">
        <div class="store-header-inner">
          <div class="store-header-brand">
            <img
              :src="storeLogoUrl"
              alt="logo"
              class="store-header-logo"
              @error="onLogoError"
            />
            <div class="store-header-titles">
              <h1 class="store-header-name">{{ seller.name }}</h1>
              <p class="store-header-sub">Catálogo de productos</p>
            </div>
          </div>
          <button
            type="button"
            class="store-cart-button"
            :class="{ 'store-cart-button--bounce': floatingBarBounce }"
            aria-label="Ver carrito"
            @click="showCheckout = true"
          >
            <i class="fas fa-shopping-cart"></i>
            <span v-if="cartCount > 0" class="store-cart-button-badge">{{ cartCount }}</span>
          </button>
        </div>
        <div class="store-header-search">
          <div class="store-search">
            <i class="fas fa-search store-search-icon"></i>
            <input
              v-model="search"
              type="text"
              class="store-search-input"
              placeholder="Buscar productos..."
              @input="onSearch"
            />
          </div>
        </div>
      </header>

      <main class="store-main">
        <div class="store-toolbar">
          <p class="store-toolbar-count">
            {{ pagination.total || 0 }}
            {{ pagination.total === 1 ? "producto" : "productos" }}
          </p>
          <div class="store-toolbar-actions">
            <button
              type="button"
              class="store-filter-toggle"
              :class="{ 'store-filter-toggle--active': showMobileFilters }"
              @click="showMobileFilters = !showMobileFilters"
            >
              <i class="fas fa-filter"></i>
            </button>
            <div class="store-filter-selects" :class="{ 'store-filter-selects--open': showMobileFilters }">
              <select
                v-if="allowedPriceLists.length > 1"
                v-model="priceList"
                class="store-select"
                @change="onPriceListChange"
              >
                <option v-if="allowedPriceLists.includes(1)" :value="1">Franquicia</option>
                <option v-if="allowedPriceLists.includes(2)" :value="2">Mayorista</option>
              </select>
              <select v-model="familiaFilter" class="store-select" @change="onFilterChange">
                <option value="">Todas las familias</option>
                <option v-for="familia in familias" :key="familia" :value="familia">
                  {{ familia }}
                </option>
              </select>
              <select v-model="lineaFilter" class="store-select" @change="onFilterChange">
                <option value="">Todas las líneas</option>
                <option v-for="linea in lineas" :key="linea" :value="linea">
                  {{ linea }}
                </option>
              </select>
              <select v-model="offerFilter" class="store-select" @change="onFilterChange">
                <option value="all">Todos</option>
                <option value="offer">En oferta</option>
              </select>
            </div>
          </div>
        </div>

        <div v-if="loadingProducts" class="store-loading store-loading--grid">
          <i class="fas fa-spinner fa-spin fa-2x"></i>
        </div>

        <div v-else class="store-grid">
          <article
            v-for="product in products"
            :key="product.id"
            class="store-card"
            :class="{ 'store-card--sold-out': !canAddProduct(product) }"
            @click="canAddProduct(product) && addToCart(product)"
          >
            <div class="store-card-media">
              <div class="store-card-badges">
                <span v-if="hasOffer(product)" class="store-badge store-badge--sale">
                  Oferta -{{ formatDiscountPercent(product) }}%
                </span>
                <span v-if="!canAddProduct(product)" class="store-badge">Agotado</span>
              </div>
              <img
                v-if="productImageUrl(product)"
                :src="productImageUrl(product)"
                :alt="product.name"
                class="store-card-img"
                loading="lazy"
                @error="onProductImageError($event, product)"
              />
              <div v-else class="store-card-placeholder">
                <i class="fas fa-box-open"></i>
              </div>
              <button
                type="button"
                class="store-card-add"
                :disabled="!canAddProduct(product)"
                @click.stop="addToCart(product)"
              >
                <i class="fas fa-plus"></i>
                <span>Añadir</span>
              </button>
            </div>
            <div class="store-card-info">
              <p class="store-card-vendor">Ref. {{ product.ref || "—" }}</p>
              <h3 class="store-card-title">{{ product.name }}</h3>
              <p v-if="lowStockNote(product)" class="store-card-stock">
                {{ lowStockNote(product) }}
              </p>
              <div class="store-card-price">
                <template v-if="hasOffer(product)">
                  <span class="store-price-compare">
                    {{ formatCurrency(getNormalPrice(product)) }}
                  </span>
                  <span class="store-price-current">
                    {{ formatCurrency(getDiscountedUnitPrice(product)) }}
                  </span>
                </template>
                <template v-else>
                  <span class="store-price-current">
                    {{ formatCurrency(getNormalPrice(product)) }}
                  </span>
                </template>
              </div>
              <p v-if="shouldShowMinUnitsMessage(product)" class="store-card-offer-note">
                {{ offerMinUnitsMessage(product) }}
              </p>
            </div>
          </article>
        </div>

        <div
          v-if="!loadingProducts && products.length === 0"
          class="store-empty"
        >
          <i class="fas fa-box-open"></i>
          <p>{{ emptyProductsMessage }}</p>
        </div>

        <div v-if="!loadingProducts && pagination.total > 0" class="store-pagination">
          <span class="store-pagination-info">
            Mostrando {{ pagination.from || 0 }}–{{ pagination.to || 0 }} de
            {{ pagination.total || 0 }}
          </span>
          <div class="store-pagination-nav">
            <button
              type="button"
              class="store-pagination-link"
              :disabled="pagination.currentPage <= 1"
              @click="changePage(pagination.currentPage - 1)"
            >
              &lsaquo; Anterior
            </button>
            <span class="store-pagination-label">
              Página {{ pagination.currentPage }} de {{ pagination.lastPage || 1 }}
            </span>
            <button
              type="button"
              class="store-pagination-link"
              :disabled="pagination.currentPage >= pagination.lastPage"
              @click="changePage(pagination.currentPage + 1)"
            >
              Siguiente &rsaquo;
            </button>
          </div>
        </div>
      </main>

      <transition name="store-backdrop-fade">
        <div v-if="showCheckout" class="store-checkout-backdrop" @click.self="closeCheckout">
          <transition name="store-panel-slide" appear>
            <div class="store-checkout" @click.stop>
              <div class="store-checkout-header">
                <h6 class="store-checkout-title">Tu pedido</h6>
                <button type="button" class="btn-close" @click="closeCheckout"></button>
              </div>
              <div class="store-checkout-body">
                <div v-if="cart.length === 0" class="store-empty store-empty--drawer">
                  <i class="fas fa-shopping-cart"></i>
                  <p>No hay productos seleccionados.</p>
                </div>
                <div v-else class="store-cart-items">
                  <div
                    v-for="(item, index) in cart"
                    :key="item.inventory_id"
                    class="store-cart-item"
                  >
                    <div class="store-cart-thumb">
                      <img
                        v-if="cartItemImage(item)"
                        :src="cartItemImage(item)"
                        :alt="item.name"
                      />
                      <i v-else class="fas fa-box-open"></i>
                    </div>
                    <div class="store-cart-item-main">
                      <div class="store-cart-item-top">
                        <span class="store-cart-item-name">{{ item.name }}</span>
                        <button
                          type="button"
                          class="store-cart-item-remove"
                          aria-label="Quitar producto"
                          @click="removeFromCart(index)"
                        >
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                      <p class="store-cart-item-ref">Ref. {{ item.ref || "—" }}</p>
                      <div
                        v-if="cartItemHasOffer(item)"
                        class="store-cart-item-offer"
                      >
                        <span class="store-badge store-badge--sale">Oferta -{{ formatDiscountPercent(item) }}%</span>
                      </div>
                      <div class="store-cart-item-bottom">
                        <div class="store-qty">
                          <button type="button" class="store-qty-btn" @click="changeQty(index, -1)">−</button>
                          <input
                            type="text"
                            class="store-qty-value"
                            inputmode="numeric"
                            :value="item.quantity"
                            @change="onQtyInput(index, $event)"
                            @focus="$event.target.select()"
                          />
                          <button type="button" class="store-qty-btn" @click="changeQty(index, 1)">+</button>
                        </div>
                        <div class="store-cart-item-prices">
                          <template v-if="cartItemHasOffer(item) && discountAppliesToItem(item)">
                            <span class="store-price-compare">
                              {{ formatCurrency(item.normal_unit_price) }}
                            </span>
                          </template>
                          <span class="store-cart-item-total">
                            {{ formatCurrency(lineTotal(item)) }}
                          </span>
                        </div>
                      </div>
                      <p
                        v-if="shouldShowMinUnitsMessage(item)"
                        class="store-card-offer-note store-card-offer-note--cart"
                      >
                        {{ offerMinUnitsMessage(item) }}
                      </p>
                    </div>
                  </div>
                </div>

                <div v-if="cart.length > 0" class="store-cart-summary">
                  <div class="store-summary-row">
                    <span>Subtotal</span>
                    <span class="store-summary-value">{{ formatCurrency(cartTotal) }}</span>
                  </div>
                  <p class="store-summary-note">
                    Impuestos y envío calculados al confirmar tu pedido.
                  </p>

                  <div class="store-contact-form">
                    <p class="store-contact-title">Datos de contacto</p>

                    <div class="store-delivery-options">
                      <label
                        class="store-delivery-option"
                        :class="{ 'store-delivery-option--active': orderForm.deliveryType === 'domicilio' }"
                        @click="orderForm.deliveryType = 'domicilio'"
                      >
                        <span class="store-delivery-option-icon"><i class="fas fa-truck"></i></span>
                        <span class="store-delivery-option-label">Domicilio</span>
                        <span class="store-delivery-option-radio">
                          <span v-if="orderForm.deliveryType === 'domicilio'" class="store-delivery-radio-dot"></span>
                        </span>
                      </label>
                      <label
                        class="store-delivery-option"
                        :class="{ 'store-delivery-option--active': orderForm.deliveryType === 'recoger' }"
                        @click="orderForm.deliveryType = 'recoger'"
                      >
                        <span class="store-delivery-option-icon"><i class="fas fa-shopping-bag"></i></span>
                        <span class="store-delivery-option-label">Para llevar / Recoger</span>
                        <span class="store-delivery-option-radio">
                          <span v-if="orderForm.deliveryType === 'recoger'" class="store-delivery-radio-dot"></span>
                        </span>
                      </label>
                    </div>

                    <div class="store-field">
                      <label class="store-field-label">Nombre <span class="store-field-req">*</span></label>
                      <input
                        v-model.trim="orderForm.nombre"
                        type="text"
                        class="form-control"
                        placeholder="Tu nombre"
                      />
                    </div>

                    <div class="store-field">
                      <label class="store-field-label">Celular <span class="store-field-req">*</span></label>
                      <input
                        v-model.trim="orderForm.celular"
                        type="tel"
                        class="form-control"
                        placeholder="Número de celular"
                      />
                    </div>

                    <div v-if="orderForm.deliveryType === 'domicilio'" class="store-field">
                      <label class="store-field-label">Dirección <span class="store-field-req">*</span></label>
                      <input
                        v-model.trim="orderForm.direccion"
                        type="text"
                        class="form-control"
                        placeholder="Escribe tu dirección"
                      />
                    </div>

                    <div class="store-field">
                      <label class="store-field-label">Comentarios de la orden</label>
                      <textarea
                        v-model.trim="orderForm.comentarios"
                        class="form-control"
                        rows="2"
                        placeholder="Instrucciones adicionales..."
                      ></textarea>
                    </div>
                  </div>

                  <button
                    type="button"
                    class="store-order-btn"
                    :disabled="!seller.phone || cart.length === 0"
                    @click="sendWhatsAppOrder"
                  >
                    <i class="fab fa-whatsapp"></i> Hacer pedido
                  </button>
                  <p v-if="!seller.phone" class="store-order-warning">
                    El vendedor no tiene teléfono registrado.
                  </p>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </transition>
    </template>
  </div>
</template>

<script>
import storeService from "@/services/store.service";
import Swal from "sweetalert2";
import logoDefault from "@/assets/img/icon.png";
import { playCartAddSound, closeCartAddSound } from "@/utils/cartAddSound";

export default {
  name: "PublicStore",
  data() {
    return {
      sellerToken: "",
      seller: { name: "", phone: "", logo_url: null, company_name: "" },
      sellerError: "",
      loadingSeller: true,
      products: [],
      loadingProducts: false,
      search: "",
      showMobileFilters: false,
      offerFilter: "all",
      familiaFilter: "",
      lineaFilter: "",
      familias: [],
      lineas: [],
      priceList: 1,
      searchTimer: null,
      pagination: {
        currentPage: 1,
        lastPage: 1,
        total: 0,
        from: 0,
        to: 0,
      },
      cart: [],
      showCheckout: false,
      floatingBarBounce: false,
      orderForm: {
        deliveryType: "domicilio",
        nombre: "",
        celular: "",
        direccion: "",
        comentarios: "",
      },
      brokenImages: {},
      logoBroken: false,
    };
  },
  computed: {
    storeLogoUrl() {
      if (this.logoBroken || !this.seller.logo_url) {
        return logoDefault;
      }
      return this.seller.logo_url;
    },
    cartCount() {
      return this.cart.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
    },
    cartTotal() {
      return this.cart.reduce((sum, item) => sum + this.lineTotal(item), 0);
    },
    emptyProductsMessage() {
      if (this.offerFilter === "offer") {
        return "No hay productos en oferta";
      }
      if (this.familiaFilter || this.lineaFilter) {
        return "No hay productos con los filtros seleccionados";
      }
      return "No hay productos disponibles";
    },
    allowedPriceLists() {
      const lists = Array.isArray(this.seller?.price_lists) ? this.seller.price_lists : [];
      const normalized = [...new Set(
        lists.map((l) => parseInt(l, 10)).filter((l) => l === 1 || l === 2)
      )].sort();
      return normalized.length ? normalized : [1];
    },
  },
  methods: {
    async initStore() {
      this.sellerToken = String(this.$route.query.seller || "").trim();
      this.logoBroken = false;
      if (!this.sellerToken) {
        this.sellerError = "Enlace inválido. Falta el identificador del vendedor.";
        this.loadingSeller = false;
        return;
      }

      try {
        const res = await storeService.getSeller(this.sellerToken);
        this.seller = res.data || {};
        this.priceList = this.allowedPriceLists[0];
        this.loadingSeller = false;
        await Promise.all([this.loadFilters(), this.loadProducts(1)]);
      } catch (error) {
        this.sellerError =
          error?.response?.data?.message || "No se pudo cargar la tienda del vendedor.";
        this.loadingSeller = false;
      }
    },
    async loadFilters() {
      try {
        const res = await storeService.getFilters(this.sellerToken);
        this.familias = Array.isArray(res.data?.familias) ? res.data.familias : [];
        this.lineas = Array.isArray(res.data?.lineas) ? res.data.lineas : [];
      } catch (error) {
        console.error("No se pudieron cargar los filtros de la tienda:", error);
        this.familias = [];
        this.lineas = [];
      }
    },
    async loadProducts(page = 1) {
      this.loadingProducts = true;
      try {
        const res = await storeService.getProducts(this.sellerToken, {
          page,
          per_page: 12,
          search: this.search || undefined,
          on_offer: this.offerFilter === "offer" ? 1 : undefined,
          familia: this.familiaFilter || undefined,
          linea: this.lineaFilter || undefined,
        });
        const data = res.data || {};
        this.products = Array.isArray(data.data) ? data.data : [];
        this.pagination = {
          currentPage: data.current_page || 1,
          lastPage: data.last_page || 1,
          total: data.total || 0,
          from: data.from || 0,
          to: data.to || 0,
        };
      } catch (error) {
        this.products = [];
        Swal.fire(
          "Error",
          error?.response?.data?.message || "No se pudieron cargar los productos.",
          "error"
        );
      } finally {
        this.loadingProducts = false;
      }
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.loadProducts(1), 350);
    },
    onFilterChange() {
      this.loadProducts(1);
    },
    changePage(page) {
      if (page < 1 || page > this.pagination.lastPage) return;
      this.loadProducts(page);
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    getNormalPrice(product) {
      const field = `precio${this.priceList}`;
      const listPrice = parseFloat(product?.[field]);
      if (!Number.isNaN(listPrice) && listPrice > 0) {
        return listPrice;
      }
      // Fallback a precio1 si la lista elegida no tiene precio en el producto.
      const fallback = parseFloat(product?.precio1);
      if (!Number.isNaN(fallback)) {
        return fallback;
      }
      const fromCart = parseFloat(product?.normal_unit_price ?? product?.unit_price);
      return Number.isNaN(fromCart) ? 0 : fromCart;
    },
    onPriceListChange() {
      // Recalcula los precios de los ítems del carrito con la lista elegida.
      this.cart.forEach((item) => {
        const product = this.products.find((p) => p.id === item.inventory_id);
        if (product) {
          const price = this.getNormalPrice(product);
          item.normal_unit_price = price;
          item.unit_price = price;
        }
      });
    },
    formatDiscountPercent(product) {
      const discount = parseFloat(product?.descuento);
      if (Number.isNaN(discount) || discount <= 0) {
        return 0;
      }
      return Number.isInteger(discount) ? discount : discount.toFixed(2).replace(/\.?0+$/, "");
    },
    getDiscountPercent(product) {
      const discount = parseFloat(product?.descuento);
      return Number.isNaN(discount) || discount <= 0 ? 0 : discount;
    },
    getMinUnitsForDiscount(product) {
      const raw = product?.descuento_min_unidades ?? product?.descuentoMinUnidades;
      const minUnits = parseInt(String(raw ?? "").trim(), 10);
      return Number.isNaN(minUnits) || minUnits < 1 ? 1 : minUnits;
    },
    shouldShowMinUnitsMessage(product) {
      return this.getDiscountPercent(product) > 0 && this.getMinUnitsForDiscount(product) > 1;
    },
    discountAppliesToItem(item) {
      if (!this.cartItemHasOffer(item)) {
        return false;
      }
      const qty = parseFloat(item.quantity) || 0;
      return qty >= this.getMinUnitsForDiscount(item);
    },
    hasOffer(product) {
      return this.getDiscountPercent(product) > 0;
    },
    cartItemHasOffer(item) {
      return this.getDiscountPercent(item) > 0;
    },
    getDiscountedUnitPrice(product) {
      const normal = this.getNormalPrice(product);
      const discount = this.getDiscountPercent(product);
      if (discount <= 0) {
        return normal;
      }
      return normal * (1 - discount / 100);
    },
    getEffectiveUnitPrice(item) {
      const normal = parseFloat(item.normal_unit_price ?? item.unit_price) || 0;
      if (!this.cartItemHasOffer(item)) {
        return normal;
      }
      const qty = parseFloat(item.quantity) || 0;
      if (qty >= this.getMinUnitsForDiscount(item)) {
        return this.getDiscountedUnitPrice(item);
      }
      return normal;
    },
    offerMinUnitsMessage(product) {
      if (!this.shouldShowMinUnitsMessage(product)) {
        return "";
      }
      const minUnits = this.getMinUnitsForDiscount(product);
      return `A partir de ${minUnits} unidades`;
    },
    lowStockNote(product) {
      if (!product.manage_stock) {
        return "";
      }
      const qty = Math.floor(product.quantity || 0);
      if (qty <= 0 || qty > 5) {
        return "";
      }
      return `Solo quedan ${qty}`;
    },
    productImageUrl(product) {
      if (this.brokenImages[product.id]) return null;
      return product.image_url || null;
    },
    cartItemImage(item) {
      const product = this.products.find((p) => p.id === item.inventory_id);
      return product ? product.image_url || null : null;
    },
    onProductImageError(_event, product) {
      this.brokenImages = { ...this.brokenImages, [product.id]: true };
    },
    onLogoError() {
      this.logoBroken = true;
    },
    canAddProduct(product) {
      if (!product.manage_stock) return true;
      return (product.quantity || 0) > 0;
    },
    maxQuantity(product) {
      if (!product.manage_stock) return 9999;
      return Math.max(1, Math.floor(product.quantity || 0));
    },
    addToCart(product) {
      if (!this.canAddProduct(product)) {
        return;
      }

      const existing = this.cart.find((item) => item.inventory_id === product.id);
      if (existing) {
        const nextQty = (parseFloat(existing.quantity) || 0) + 1;
        if (product.manage_stock && nextQty > existing.max_quantity) {
          Swal.fire("Stock limitado", `Solo hay ${existing.max_quantity} unidad(es) disponibles.`, "warning");
          return;
        }
        existing.quantity = nextQty;
        existing.normal_unit_price = this.getNormalPrice(product);
        existing.descuento = this.getDiscountPercent(product);
        existing.descuento_min_unidades = this.getMinUnitsForDiscount(product);
      } else {
        const normalPrice = this.getNormalPrice(product);
        this.cart.push({
          inventory_id: product.id,
          name: product.name,
          ref: product.ref,
          normal_unit_price: normalPrice,
          unit_price: normalPrice,
          descuento: this.getDiscountPercent(product),
          descuento_min_unidades: this.getMinUnitsForDiscount(product),
          quantity: 1,
          max_quantity: this.maxQuantity(product),
        });
      }

      this.triggerCartAddedFeedback();
    },
    triggerCartAddedFeedback() {
      playCartAddSound();
      this.floatingBarBounce = false;
      this.$nextTick(() => {
        this.floatingBarBounce = true;
        if (this._bounceTimer) {
          clearTimeout(this._bounceTimer);
        }
        this._bounceTimer = setTimeout(() => {
          this.floatingBarBounce = false;
        }, 480);
      });
    },
    onQtyInput(index, event) {
      const item = this.cart[index];
      let val = parseFloat(event.target.value);
      if (isNaN(val) || val < 1) {
        val = 1;
      }
      if (item.max_quantity && val > item.max_quantity) {
        val = item.max_quantity;
        Swal.fire("Stock limitado", `Solo hay ${item.max_quantity} unidad(es) disponibles.`, "warning");
      }
      const product = this.products.find((p) => p.id === item.inventory_id);
      if (product) {
        item.normal_unit_price = this.getNormalPrice(product);
        item.descuento = this.getDiscountPercent(product);
        item.descuento_min_unidades = this.getMinUnitsForDiscount(product);
      }
      item.quantity = val;
      event.target.value = val;
    },
    changeQty(index, delta) {
      const item = this.cart[index];
      const next = (parseFloat(item.quantity) || 0) + delta;
      if (next < 1) {
        this.removeFromCart(index);
        return;
      }
      if (item.max_quantity && next > item.max_quantity) {
        Swal.fire("Stock limitado", `Solo hay ${item.max_quantity} unidad(es) disponibles.`, "warning");
        return;
      }
      const product = this.products.find((p) => p.id === item.inventory_id);
      if (product) {
        item.normal_unit_price = this.getNormalPrice(product);
        item.descuento = this.getDiscountPercent(product);
        item.descuento_min_unidades = this.getMinUnitsForDiscount(product);
      }
      item.quantity = next;
    },
    removeFromCart(index) {
      this.cart.splice(index, 1);
      if (this.cart.length === 0) {
        this.showCheckout = false;
      }
    },
    lineTotal(item) {
      const qty = parseFloat(item.quantity) || 0;
      return qty * this.getEffectiveUnitPrice(item);
    },
    formatCurrency(value) {
      return `$ ${parseFloat(value || 0).toLocaleString("es-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    closeCheckout() {
      this.showCheckout = false;
      this.orderForm = {
        deliveryType: "domicilio",
        nombre: "",
        celular: "",
        direccion: "",
        comentarios: "",
      };
    },
    normalizePhone(phone) {
      const digits = String(phone || "").replace(/\D/g, "");
      if (!digits) return "";
      if (digits.length === 10) {
        return `57${digits}`;
      }
      return digits;
    },
    buildWhatsAppMessage() {
      const productLines = this.cart.map((item) => {
        const qty = parseFloat(item.quantity) || 0;
        const total = this.lineTotal(item);
        const unitPrice = this.getEffectiveUnitPrice(item);
        const offerNote = this.cartItemHasOffer(item)
          ? ` (${this.offerMinUnitsMessage(item)}, ${this.formatCurrency(unitPrice)}/u)`
          : "";
        return `- ${item.name} (Ref: ${item.ref || "—"}) x${qty} = ${this.formatCurrency(total)}${offerNote}`;
      });

      const deliveryLabel =
        this.orderForm.deliveryType === "domicilio" ? "Domicilio" : "Para llevar / Recoger";

      const lines = [
        "*Nuevo pedido desde tienda*",
        "",
        `Vendedor: ${this.seller.name}`,
        "",
        "*Datos del cliente:*",
        `Nombre: ${this.orderForm.nombre}`,
        `Celular: ${this.orderForm.celular}`,
        `Entrega: ${deliveryLabel}`,
      ];

      if (this.orderForm.deliveryType === "domicilio" && this.orderForm.direccion) {
        lines.push(`Dirección: ${this.orderForm.direccion}`);
      }

      if (this.orderForm.comentarios) {
        lines.push(`Comentarios: ${this.orderForm.comentarios}`);
      }

      lines.push(
        "",
        "*Productos:*",
        "",
        productLines.join("\n\n"),
        "",
        `*Total: ${this.formatCurrency(this.cartTotal)}*`
      );

      return lines.join("\r\n");
    },
    async sendWhatsAppOrder() {
      const phone = this.normalizePhone(this.seller.phone);
      if (!phone) {
        Swal.fire("Sin teléfono", "El vendedor no tiene WhatsApp configurado.", "warning");
        return;
      }

      if (!this.orderForm.nombre) {
        Swal.fire("Datos incompletos", "Por favor ingresa tu nombre.", "warning");
        return;
      }
      if (!this.orderForm.celular) {
        Swal.fire("Datos incompletos", "Por favor ingresa tu número de celular.", "warning");
        return;
      }
      if (this.orderForm.deliveryType === "domicilio" && !this.orderForm.direccion) {
        Swal.fire("Datos incompletos", "Por favor ingresa tu dirección de entrega.", "warning");
        return;
      }

      // Safari/iOS bloquea window.open() si se ejecuta después de un await.
      // Abrimos la ventana de inmediato (dentro del gesto del usuario) y luego
      // le asignamos la URL de WhatsApp cuando el pedido ya se registró.
      const waWindow = window.open("about:blank", "_blank");

      const payload = {
        nombre: this.orderForm.nombre,
        celular: this.orderForm.celular,
        direccion: this.orderForm.direccion,
        comentarios: this.orderForm.comentarios,
        delivery_type: this.orderForm.deliveryType,
        price_list: this.priceList,
        items: this.cart.map((item) => ({
          inventory_id: item.inventory_id,
          quantity: parseFloat(item.quantity) || 0,
        })),
      };

      let saved = false;
      try {
        await storeService.createCotizacion(this.sellerToken, payload);
        saved = true;
      } catch (error) {
        console.error("No se pudo registrar la cotización:", error);
      }

      const text = encodeURIComponent(this.buildWhatsAppMessage());
      const waUrl = `https://wa.me/${phone}?text=${text}`;
      if (waWindow) {
        waWindow.opener = null;
        waWindow.location.href = waUrl;
      } else {
        // Fallback si el navegador bloqueó la ventana previa.
        window.location.assign(waUrl);
      }

      if (saved) {
        this.cart = [];
        this.closeCheckout();
        Swal.fire(
          "¡Pedido enviado!",
          "Tu pedido fue registrado como cotización y enviado al vendedor.",
          "success"
        );
      }
    },
  },
  mounted() {
    this.$store.state.showNavbar = false;
    this.$store.state.showSidenav = false;
    this.$store.state.showFooter = false;
    this.$store.state.hideConfigButton = true;
    this.initStore();
  },
  beforeUnmount() {
    if (this._bounceTimer) {
      clearTimeout(this._bounceTimer);
    }
    closeCartAddSound();
    this.$store.state.showNavbar = true;
    this.$store.state.showSidenav = true;
    this.$store.state.showFooter = true;
    this.$store.state.hideConfigButton = false;
  },
  watch: {
    "$route.query.seller"(value) {
      if (value && value !== this.sellerToken) {
        this.cart = [];
        this.showCheckout = false;
        this.initStore();
      }
    },
  },
};
</script>

<style scoped>
.store-page {
  min-height: 100vh;
  background: #fff;
  color: #121212;
  padding-bottom: 4rem;
}

/* ---------- Header ---------- */
.store-header {
  position: sticky;
  top: 0;
  z-index: 1030;
  background: #fff;
  border-bottom: 1px solid rgba(18, 18, 18, 0.08);
  padding: 0.85rem 1.25rem 0.9rem;
}

.store-header-inner {
  max-width: 1240px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.store-header-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.store-header-logo {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  object-fit: contain;
  background: #f6f6f6;
  padding: 4px;
  flex-shrink: 0;
}

.store-header-titles {
  min-width: 0;
}

.store-header-name {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #121212;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.store-header-sub {
  margin: 0;
  font-size: 0.75rem;
  color: rgba(18, 18, 18, 0.55);
}

.store-cart-button {
  position: relative;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid rgba(18, 18, 18, 0.15);
  background: #fff;
  color: #121212;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease, color 0.15s ease;
}

.store-cart-button:hover {
  background: #121212;
  color: #fff;
}

.store-cart-button-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 999px;
  background: #121212;
  color: #fff;
  border: 2px solid #fff;
  font-size: 0.65rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.store-cart-button--bounce {
  animation: storeCartBounce 0.45s ease;
}

@keyframes storeCartBounce {
  0%,
  100% {
    transform: scale(1);
  }
  35% {
    transform: scale(1.18);
  }
  55% {
    transform: scale(0.94);
  }
  75% {
    transform: scale(1.06);
  }
}

.store-header-search {
  max-width: 1240px;
  margin: 0.8rem auto 0;
}

.store-search {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  border: 1px solid rgba(18, 18, 18, 0.2);
  border-radius: 999px;
  padding: 0.5rem 1.1rem;
  background: #fff;
  transition: border-color 0.15s ease;
}

.store-search:focus-within {
  border-color: #121212;
}

.store-search-icon {
  color: rgba(18, 18, 18, 0.5);
  font-size: 0.85rem;
}

.store-search-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.9rem;
  color: #121212;
}

.store-search-input::placeholder {
  color: rgba(18, 18, 18, 0.45);
}

/* ---------- Main / toolbar ---------- */
.store-main {
  max-width: 1240px;
  margin: 0 auto;
  padding: 1.4rem 1.25rem 0;
}

.store-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}

.store-toolbar-count {
  margin: 0;
  font-size: 0.85rem;
  color: rgba(18, 18, 18, 0.6);
}

.store-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.store-select {
  border: 1px solid rgba(18, 18, 18, 0.2);
  border-radius: 8px;
  background: #fff;
  color: #121212;
  font-size: 0.8rem;
  padding: 0.42rem 0.6rem;
  outline: none;
  cursor: pointer;
}

.store-select:focus {
  border-color: #121212;
}

.store-filter-toggle {
  display: none;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(18, 18, 18, 0.2);
  border-radius: 8px;
  background: #fff;
  color: #121212;
  font-size: 0.8rem;
  cursor: pointer;
}

.store-filter-toggle--active {
  background: #121212;
  color: #fff;
  border-color: #121212;
}

.store-filter-selects {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

@media (max-width: 575.98px) {
  .store-filter-toggle {
    display: inline-flex;
  }

  .store-filter-selects {
    flex-basis: 100%;
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.25s ease;
  }

  .store-filter-selects--open {
    max-height: 320px;
  }

  .store-filter-selects .store-select {
    flex: 1 1 calc(50% - 0.25rem);
    min-width: 0;
  }
}

/* ---------- Grid de productos ---------- */
.store-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem 0.85rem;
}

@media (min-width: 480px) {
  .store-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 768px) {
  .store-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem 1rem;
  }
}

@media (min-width: 1100px) {
  .store-grid {
    grid-template-columns: repeat(5, 1fr);
    gap: 1.75rem 1.1rem;
  }
}

.store-card {
  display: flex;
  flex-direction: column;
  cursor: pointer;
  min-width: 0;
}

.store-card--sold-out {
  cursor: not-allowed;
}

.store-card-media {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  overflow: hidden;
  background: #f4f5f7;
  display: flex;
  align-items: center;
  justify-content: center;
}

.store-card-img {
  display: block;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  object-position: center;
  transition: transform 0.35s ease;
}

.store-card:hover .store-card-img {
  transform: scale(1.04);
}

.store-card-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: rgba(18, 18, 18, 0.25);
  font-size: 2rem;
}

.store-card-badges {
  position: absolute;
  top: 0.55rem;
  left: 0.55rem;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.4rem;
  pointer-events: none;
}

.store-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  background: #fff;
  border: 1px solid rgba(18, 18, 18, 0.12);
  color: #121212;
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  line-height: 1.1;
  white-space: nowrap;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.store-badge--sale {
  color: #b3261e;
}

.store-card-add {
  position: absolute;
  left: 0.55rem;
  right: 0.55rem;
  bottom: 0.55rem;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.55rem 0.75rem;
  border: 1px solid rgba(18, 18, 18, 0.12);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(4px);
  color: #121212;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 0.2s ease, transform 0.2s ease, background 0.15s ease, color 0.15s ease;
}

.store-card:hover .store-card-add:not(:disabled) {
  opacity: 1;
  transform: translateY(0);
}

.store-card-add:not(:disabled):hover {
  background: #121212;
  color: #fff;
  border-color: #121212;
}

.store-card-add:disabled {
  cursor: default;
}

@media (hover: none) {
  .store-card-add {
    opacity: 1;
    transform: none;
  }
}

.store-card-info {
  padding: 0.75rem 0.2rem 0;
  text-align: left;
}

.store-card-vendor {
  margin: 0 0 0.2rem;
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(18, 18, 18, 0.5);
}

.store-card-title {
  margin: 0 0 0.35rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #121212;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.store-card-stock {
  margin: 0 0 0.3rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #b3261e;
}

.store-card-price {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.store-price-current {
  font-size: 0.95rem;
  font-weight: 600;
  color: #121212;
}

.store-price-compare {
  font-size: 0.78rem;
  color: rgba(18, 18, 18, 0.45);
  text-decoration: line-through;
}

.store-card-offer-note {
  margin: 0.25rem 0 0;
  font-size: 0.68rem;
  font-weight: 500;
  color: rgba(18, 18, 18, 0.55);
  line-height: 1.3;
}

.store-card-offer-note--cart {
  text-align: left;
}

/* ---------- Estados ---------- */
.store-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5rem 1rem;
  color: rgba(18, 18, 18, 0.4);
}

.store-loading--grid {
  padding: 3rem 1rem;
}

.store-error {
  max-width: 420px;
  margin: 4rem auto;
  padding: 2.5rem 1.5rem;
  text-align: center;
  border: 1px solid rgba(18, 18, 18, 0.1);
  border-radius: 16px;
}

.store-error i {
  font-size: 2.2rem;
  color: rgba(18, 18, 18, 0.3);
  margin-bottom: 1rem;
}

.store-error h5 {
  font-weight: 700;
  color: #121212;
}

.store-error p {
  color: rgba(18, 18, 18, 0.55);
  margin-bottom: 0;
}

.store-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3.5rem 1rem;
  color: rgba(18, 18, 18, 0.5);
  text-align: center;
}

.store-empty i {
  font-size: 2rem;
  color: rgba(18, 18, 18, 0.25);
}

.store-empty p {
  margin: 0;
  font-size: 0.9rem;
}

.store-empty--drawer {
  padding: 2.5rem 1rem;
}

/* ---------- Paginación ---------- */
.store-pagination {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin-top: 2.5rem;
  text-align: center;
}

.store-pagination-info {
  font-size: 0.78rem;
  color: rgba(18, 18, 18, 0.5);
}

.store-pagination-nav {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.store-pagination-link {
  border: none;
  background: none;
  padding: 0;
  font-size: 0.9rem;
  color: #121212;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

.store-pagination-link:hover:not(:disabled) {
  color: rgba(18, 18, 18, 0.65);
}

.store-pagination-link:disabled {
  color: rgba(18, 18, 18, 0.25);
  text-decoration: none;
  cursor: default;
}

.store-pagination-label {
  font-size: 0.82rem;
  color: rgba(18, 18, 18, 0.55);
}

/* ---------- Drawer del carrito ---------- */
.store-checkout-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1050;
  background: rgba(18, 18, 18, 0.4);
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
}

.store-checkout {
  width: 100%;
  max-width: 430px;
  height: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.store-checkout-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(18, 18, 18, 0.08);
  flex-shrink: 0;
}

.store-checkout-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #121212;
}

.store-checkout-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem;
}

.store-cart-items {
  display: flex;
  flex-direction: column;
}

.store-cart-item {
  display: flex;
  gap: 0.85rem;
  padding: 0.9rem 0;
  border-bottom: 1px solid rgba(18, 18, 18, 0.08);
}

.store-cart-thumb {
  width: 60px;
  height: 60px;
  border-radius: 10px;
  background: #f2f2f2;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(18, 18, 18, 0.3);
}

.store-cart-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.store-cart-item-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.store-cart-item-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}

.store-cart-item-name {
  font-size: 0.83rem;
  font-weight: 600;
  color: #121212;
  line-height: 1.3;
  min-width: 0;
}

.store-cart-item-remove {
  border: none;
  background: transparent;
  color: rgba(18, 18, 18, 0.4);
  padding: 0;
  font-size: 0.85rem;
  line-height: 1;
  cursor: pointer;
}

.store-cart-item-remove:hover {
  color: #b3261e;
}

.store-cart-item-ref {
  margin: 0.1rem 0 0;
  font-size: 0.7rem;
  color: rgba(18, 18, 18, 0.45);
}

.store-cart-item-offer {
  margin-top: 0.35rem;
}

.store-cart-item-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: auto;
  padding-top: 0.5rem;
}

.store-cart-item-prices {
  display: flex;
  align-items: baseline;
  gap: 0.45rem;
}

.store-cart-item-total {
  font-size: 0.88rem;
  font-weight: 700;
  color: #121212;
}

.store-qty {
  display: inline-flex;
  align-items: stretch;
  border: 1px solid rgba(18, 18, 18, 0.18);
  border-radius: 999px;
  overflow: hidden;
  background: #fff;
}

.store-qty-btn {
  border: none;
  background: transparent;
  color: #121212;
  width: 1.9rem;
  font-size: 0.95rem;
  line-height: 1;
  padding: 0.35rem 0;
  cursor: pointer;
}

.store-qty-btn:hover {
  background: #f2f2f2;
}

.store-qty-value {
  min-width: 2rem;
  width: 3rem;
  border: none;
  border-left: 1px solid rgba(18, 18, 18, 0.12);
  border-right: 1px solid rgba(18, 18, 18, 0.12);
  background: #fff;
  color: #121212;
  font-size: 0.85rem;
  font-weight: 600;
  text-align: center;
  padding: 0;
  outline: none;
}

/* ---------- Resumen / formulario ---------- */
.store-cart-summary {
  border-top: 1px solid rgba(18, 18, 18, 0.08);
  margin-top: 1.25rem;
  padding-top: 1.1rem;
}

.store-summary-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-size: 0.95rem;
  font-weight: 600;
  color: #121212;
}

.store-summary-value {
  font-size: 1.05rem;
  font-weight: 700;
}

.store-summary-note {
  margin: 0.3rem 0 0;
  font-size: 0.72rem;
  color: rgba(18, 18, 18, 0.45);
}

.store-contact-form {
  margin-top: 1.25rem;
  padding-top: 1.1rem;
  border-top: 1px solid rgba(18, 18, 18, 0.08);
}

.store-contact-title {
  margin: 0 0 0.75rem;
  font-size: 0.85rem;
  font-weight: 700;
  color: #121212;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.store-field {
  margin-bottom: 0.75rem;
}

.store-field-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(18, 18, 18, 0.7);
  margin-bottom: 0.3rem;
}

.store-field-req {
  color: #b3261e;
}

.store-contact-form .form-control {
  border: 1px solid rgba(18, 18, 18, 0.2);
  border-radius: 8px;
  font-size: 0.875rem;
  color: #121212;
  padding: 0.5rem 0.75rem;
}

.store-contact-form .form-control:focus {
  border-color: #121212;
  box-shadow: none;
}

.store-delivery-options {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.9rem;
}

.store-delivery-option {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.65rem 0.85rem;
  border: 1px solid rgba(18, 18, 18, 0.18);
  border-radius: 10px;
  cursor: pointer;
  user-select: none;
  background: #fff;
  transition: border-color 0.15s ease;
}

.store-delivery-option--active {
  border-color: #121212;
}

.store-delivery-option-icon {
  font-size: 1rem;
  color: rgba(18, 18, 18, 0.7);
  flex-shrink: 0;
}

.store-delivery-option-label {
  flex: 1;
  font-size: 0.85rem;
  font-weight: 500;
  color: #121212;
}

.store-delivery-option-radio {
  width: 1.05rem;
  height: 1.05rem;
  border: 1px solid rgba(18, 18, 18, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #fff;
}

.store-delivery-option--active .store-delivery-option-radio {
  border-color: #121212;
}

.store-delivery-radio-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #121212;
}

.store-order-btn {
  width: 100%;
  margin-top: 1rem;
  padding: 0.85rem 1rem;
  border: none;
  border-radius: 10px;
  background: #121212;
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}

.store-order-btn:hover:not(:disabled) {
  background: #000;
}

.store-order-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.store-order-btn i {
  margin-right: 0.35rem;
  font-size: 1.05rem;
}

.store-order-warning {
  margin: 0.6rem 0 0;
  font-size: 0.78rem;
  color: #b3261e;
}

/* ---------- Transiciones ---------- */
.store-backdrop-fade-enter-active,
.store-backdrop-fade-leave-active {
  transition: opacity 0.25s ease;
}

.store-backdrop-fade-enter-from,
.store-backdrop-fade-leave-to {
  opacity: 0;
}

.store-panel-slide-enter-active,
.store-panel-slide-leave-active {
  transition: transform 0.3s ease;
}

.store-panel-slide-enter-from,
.store-panel-slide-leave-to {
  transform: translateX(100%);
}
</style>
